import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Settings, Shield, Users, Gamepad2, DollarSign, Lock,
  Save, RefreshCw, AlertTriangle, CheckCircle, Info,
  Percent, Clock, Ban, UserCheck, Globe, Mail, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('platform');
  
  // Platform Settings
  const [platformSettings, setPlatformSettings] = useState({
    platformName: 'MatchStake',
    maintenanceMode: false,
    registrationEnabled: true,
    googleAuthEnabled: true,
    emailAuthEnabled: true,
    requireEmailVerification: false,
    requireKYC: false,
    creditConversionRate: 100
  });
  
  // Match Settings
  const [matchSettings, setMatchSettings] = useState({
    minEntryFee: 1,
    maxEntryFee: 500,
    matchTimeout: 120, // minutes
    proofRequired: true,
    allowCustomGames: true,
    autoDisputeOnMismatch: true
  });
  
  // Payout Settings
  const [payoutSettings, setPayoutSettings] = useState({
    withdrawalEnabled: true,
    instantPayouts: false
  });
  
  // Financial Settings (New) - prize_tiers now auto-calculated
  const [financialSettings, setFinancialSettings] = useState({
    credit_conversion_rate: 100,
    platform_fee_percent: 5.0,
    min_withdrawal: 10.0,
    max_withdrawal: 500.0,
    withdrawal_fee_fixed: 1.0,
    withdrawal_fee_percent: 1.0,
    min_deposit: 5.0,
    max_deposit: 1000.0,
    entry_fee_tiers: [5, 10, 15, 20, 25, 50, 75, 100]
  });

  // Helper to calculate auto rewards
  const calculateReward = (entryFee) => {
    const feePercent = financialSettings.platform_fee_percent || 5.0;
    const totalPot = entryFee * 2;
    const platformFee = totalPot * (feePercent / 100);
    return (totalPot - platformFee).toFixed(2);
  };

  const calculatePlatformFee = (entryFee) => {
    const feePercent = financialSettings.platform_fee_percent || 5.0;
    const totalPot = entryFee * 2;
    return (totalPot * (feePercent / 100)).toFixed(2);
  };
  
  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 30, // minutes
    maxLoginAttempts: 5,
    ipRateLimiting: true,
    requireStrongPassword: true,
    twoFactorEnabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      if (response.data) {
        // Check for new simplified settings format
        if (response.data.email_verification_required !== undefined) {
          setPlatformSettings(prev => ({ 
            ...prev, 
            requireEmailVerification: response.data.email_verification_required 
          }));
        }
        // Legacy format support
        if (response.data.platform) setPlatformSettings(prev => ({ ...prev, ...response.data.platform }));
        if (response.data.match) setMatchSettings(prev => ({ ...prev, ...response.data.match }));
        if (response.data.payout) setPayoutSettings(prev => ({ ...prev, ...response.data.payout }));
        if (response.data.security) setSecuritySettings(prev => ({ ...prev, ...response.data.security }));
        if (response.data.financial) setFinancialSettings(prev => ({ ...prev, ...response.data.financial }));
      }
    } catch (error) {
      // Settings may not exist yet, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings`, {
        platform: platformSettings,
        match: matchSettings,
        payout: payoutSettings,
        security: securitySettings,
        financial: financialSettings
      }, { withCredentials: true });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (oldEntry, newEntry) => {
    setFinancialSettings(prev => {
      const newTiers = [...prev.entry_fee_tiers];
      const index = newTiers.indexOf(oldEntry);
      if (index !== -1) {
        newTiers[index] = newEntry;
      }
      return { ...prev, entry_fee_tiers: newTiers.sort((a,b) => a-b) };
    });
  };

  const addTier = () => {
    const entry = 0;
    setFinancialSettings(prev => ({
      ...prev,
      entry_fee_tiers: [...prev.entry_fee_tiers, entry].sort((a,b) => a-b)
    }));
  };

  const removeTier = (entry) => {
    setFinancialSettings(prev => ({
      ...prev,
      entry_fee_tiers: prev.entry_fee_tiers.filter(t => t !== entry)
    }));
  };

  const sections = [
    { id: 'platform', label: 'Platform', icon: Globe },
    { id: 'matches', label: 'Match Settings', icon: Gamepad2 },
    { id: 'payouts', label: 'Financial Settings', icon: DollarSign },
    { id: 'security', label: 'Security', icon: Lock },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto">
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="admin-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#7000FF]/20 flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#7000FF]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">Platform Settings</h1>
              <p className="text-white/50">Configure your platform preferences</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchSettings} className="btn-secondary">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button onClick={saveSettings} disabled={saving} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass-card rounded-xl p-4 sticky top-4">
              <nav className="space-y-1">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-[#7000FF]/20 text-[#7000FF]'
                        : 'text-white/70 hover:bg-white/5'
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Platform Settings */}
            {activeSection === 'platform' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="font-heading font-semibold text-xl mb-6 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#00F0FF]" />
                  Platform Configuration
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">Platform Name</label>
                    <Input
                      value={platformSettings.platformName}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, platformName: e.target.value }))}
                      className="input-dark max-w-md"
                    />
                  </div>

                  <div className="p-4 rounded-lg bg-[#FFB800]/10 border border-[#FFB800]/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
                        <div>
                          <p className="font-medium">Maintenance Mode</p>
                          <p className="text-white/50 text-sm">Disable access for non-admin users</p>
                        </div>
                      </div>
                      <Switch
                        checked={platformSettings.maintenanceMode}
                        onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, maintenanceMode: checked }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">New User Registration</p>
                      <p className="text-white/50 text-sm">Allow new users to sign up</p>
                    </div>
                    <Switch
                      checked={platformSettings.registrationEnabled}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, registrationEnabled: checked }))}
                    />
                  </div>

                  <div className="p-4 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-[#00FF94]" />
                        <div>
                          <p className="font-medium">Require Email Verification</p>
                          <p className="text-white/50 text-sm">Users must verify email before creating/joining matches or withdrawing</p>
                        </div>
                      </div>
                      <Switch
                        checked={platformSettings.requireEmailVerification}
                        onCheckedChange={async (checked) => {
                          try {
                            await axios.post(`${API}/admin/settings/email-verification?enabled=${checked}`, {}, { withCredentials: true });
                            setPlatformSettings(prev => ({ ...prev, requireEmailVerification: checked }));
                            toast.success(`Email verification ${checked ? 'enabled' : 'disabled'}`);
                          } catch (error) {
                            toast.error('Failed to update setting');
                          }
                        }}
                      />
                    </div>
                    {!platformSettings.requireEmailVerification && (
                      <p className="text-[#FFB800] text-xs mt-2 ml-8">⚠️ Email verification is currently OFF - users can access all features without verifying</p>
                    )}
                  </div>

                  <div className="p-4 rounded-lg bg-white/5">
                    <label className="text-sm text-white/70 mb-2 block">Credit Conversion Rate ($1 = ? credits)</label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={financialSettings.credit_conversion_rate}
                        onChange={(e) => setFinancialSettings(prev => ({ ...prev, credit_conversion_rate: parseInt(e.target.value) || 100 }))}
                        className="input-dark max-w-[200px]"
                      />
                      <span className="text-white/50 text-sm">Affects all credit-to-cash and vice-versa translations. Default: 100</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* User Management */}
            {activeSection === 'users' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="font-heading font-semibold text-xl mb-6 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#00FF94]" />
                  User Management
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium">Google Authentication</p>
                        <p className="text-white/50 text-sm">Allow sign in with Google</p>
                      </div>
                    </div>
                    <Switch
                      checked={platformSettings.googleAuthEnabled}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, googleAuthEnabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium">Email/Password Authentication</p>
                        <p className="text-white/50 text-sm">Allow sign in with username & password</p>
                      </div>
                    </div>
                    <Switch
                      checked={platformSettings.emailAuthEnabled}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, emailAuthEnabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-5 h-5 text-[#00F0FF]" />
                      <div>
                        <p className="font-medium">Require KYC Verification</p>
                        <p className="text-white/50 text-sm">Users must verify identity before playing</p>
                      </div>
                    </div>
                    <Switch
                      checked={platformSettings.requireKYC}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, requireKYC: checked }))}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Match Settings */}
            {activeSection === 'matches' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="font-heading font-semibold text-xl mb-6 flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                  Match Configuration
                </h2>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm text-white/70 mb-2 block">Minimum Entry Fee ($)</label>
                      <Input
                        type="number"
                        min="1"
                        value={matchSettings.minEntryFee}
                        onChange={(e) => setMatchSettings(prev => ({ ...prev, minEntryFee: parseFloat(e.target.value) || 1 }))}
                        className="input-dark"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-white/70 mb-2 block">Maximum Entry Fee ($)</label>
                      <Input
                        type="number"
                        min="1"
                        value={matchSettings.maxEntryFee}
                        onChange={(e) => setMatchSettings(prev => ({ ...prev, maxEntryFee: parseFloat(e.target.value) || 500 }))}
                        className="input-dark"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Match Timeout: {matchSettings.matchTimeout} minutes
                    </label>
                    <Slider
                      value={[matchSettings.matchTimeout]}
                      onValueChange={([value]) => setMatchSettings(prev => ({ ...prev, matchTimeout: value }))}
                      min={30}
                      max={240}
                      step={15}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-white/40 mt-1">
                      <span>30 min</span>
                      <span>4 hours</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Require Proof Submission</p>
                      <p className="text-white/50 text-sm">Players must submit screenshot/video proof</p>
                    </div>
                    <Switch
                      checked={matchSettings.proofRequired}
                      onCheckedChange={(checked) => setMatchSettings(prev => ({ ...prev, proofRequired: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Allow Custom Games</p>
                      <p className="text-white/50 text-sm">Users can create matches for games not in the list</p>
                    </div>
                    <Switch
                      checked={matchSettings.allowCustomGames}
                      onCheckedChange={(checked) => setMatchSettings(prev => ({ ...prev, allowCustomGames: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Auto-Dispute on Mismatch</p>
                      <p className="text-white/50 text-sm">Automatically create dispute if both claim win</p>
                    </div>
                    <Switch
                      checked={matchSettings.autoDisputeOnMismatch}
                      onCheckedChange={(checked) => setMatchSettings(prev => ({ ...prev, autoDisputeOnMismatch: checked }))}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Payout Settings */}
            {activeSection === 'payouts' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="font-heading font-semibold text-xl mb-6 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#CCFF00]" />
                  Financial Configuration
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-white/5">
                    <div>
                      <label className="text-sm text-white/70 mb-2 block">
                        Platform Match Fee: {financialSettings.platform_fee_percent}%
                      </label>
                      <Slider
                        value={[financialSettings.platform_fee_percent]}
                        onValueChange={([value]) => setFinancialSettings(prev => ({ ...prev, platform_fee_percent: value }))}
                        min={0}
                        max={20}
                        step={0.5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-white/40 mt-1">
                        <span>0%</span>
                        <span>20%</span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Currency Conversion</h3>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Credits per $1</label>
                        <Input
                          type="number"
                          value={financialSettings.credit_conversion_rate}
                          onChange={(e) => setFinancialSettings(prev => ({ ...prev, credit_conversion_rate: parseInt(e.target.value) || 100 }))}
                          className="input-dark"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Withdrawal Limits</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Min ($)</label>
                          <Input
                            type="number"
                            value={financialSettings.min_withdrawal}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, min_withdrawal: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Max ($)</label>
                          <Input
                            type="number"
                            value={financialSettings.max_withdrawal}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, max_withdrawal: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Withdrawal Fees</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Fixed Fee ($)</label>
                          <Input
                            type="number"
                            value={financialSettings.withdrawal_fee_fixed}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, withdrawal_fee_fixed: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Fee %</label>
                          <Input
                            type="number"
                            value={financialSettings.withdrawal_fee_percent}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, withdrawal_fee_percent: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-white/5">
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Deposit Limits</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Min ($)</label>
                          <Input
                            type="number"
                            value={financialSettings.min_deposit}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, min_deposit: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Max ($)</label>
                          <Input
                            type="number"
                            value={financialSettings.max_deposit}
                            onChange={(e) => setFinancialSettings(prev => ({ ...prev, max_deposit: parseFloat(e.target.value) || 0 }))}
                            className="input-dark"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Fee Preview</h3>
                      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-sm text-white/70">
                          Withdrawal Fee = <span className="text-[#CCFF00]">${financialSettings.withdrawal_fee_fixed?.toFixed(2)}</span> + 
                          <span className="text-[#CCFF00]">{financialSettings.withdrawal_fee_percent}%</span>
                        </p>
                        <p className="text-xs text-white/40 mt-1 italic">
                          Calculated per withdrawal request.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Enable Withdrawals</p>
                      <p className="text-white/50 text-sm">Allow users to withdraw funds</p>
                    </div>
                    <Switch
                      checked={payoutSettings.withdrawalEnabled}
                      onCheckedChange={(checked) => setPayoutSettings(prev => ({ ...prev, withdrawalEnabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Instant Payouts</p>
                      <p className="text-white/50 text-sm">Process withdrawals immediately (requires Stripe Instant)</p>
                    </div>
                    <Switch
                      checked={payoutSettings.instantPayouts}
                      onCheckedChange={(checked) => setPayoutSettings(prev => ({ ...prev, instantPayouts: checked }))}
                    />
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-medium">Match Entry & Reward Tiers</h3>
                        <p className="text-xs text-white/50 mt-1">
                          Winner reward calculated automatically: Entry × 2 × (1 - {financialSettings.platform_fee_percent}%)
                        </p>
                      </div>
                      <Button size="sm" onClick={addTier} variant="outline" className="btn-secondary">
                        Add Tier
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {financialSettings.entry_fee_tiers.map((entry) => (
                        <div key={entry} className="flex items-center gap-4 bg-white/5 p-3 rounded-lg border border-white/5">
                          <div className="flex-1">
                            <label className="text-xs text-white/50 block mb-1">Entry Fee ($)</label>
                            <Input
                              type="number"
                              value={entry}
                              onChange={(e) => updateTier(entry, parseFloat(e.target.value) || 0)}
                              className="input-dark h-9"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-white/50 block mb-1">Winner Reward (auto)</label>
                            <div className="h-9 px-3 py-2 bg-[#00FF94]/10 border border-[#00FF94]/20 rounded-md text-[#00FF94] font-mono flex items-center">
                              ${calculateReward(entry)}
                            </div>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-white/50 block mb-1">Platform Fee (auto)</label>
                            <div className="h-9 px-3 py-2 bg-[#7000FF]/10 border border-[#7000FF]/20 rounded-md text-[#7000FF] font-mono flex items-center">
                              ${calculatePlatformFee(entry)}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeTier(entry)}
                            className="mt-5 text-[#FF2E2E] hover:bg-[#FF2E2E]/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Security Settings */}
            {activeSection === 'security' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-xl p-6"
              >
                <h2 className="font-heading font-semibold text-xl mb-6 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF2E2E]" />
                  Security Configuration
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Session Timeout: {securitySettings.sessionTimeout} minutes
                    </label>
                    <Slider
                      value={[securitySettings.sessionTimeout]}
                      onValueChange={([value]) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: value }))}
                      min={5}
                      max={120}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-white/40 mt-1">
                      <span>5 min</span>
                      <span>2 hours</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Max Login Attempts: {securitySettings.maxLoginAttempts}
                    </label>
                    <Slider
                      value={[securitySettings.maxLoginAttempts]}
                      onValueChange={([value]) => setSecuritySettings(prev => ({ ...prev, maxLoginAttempts: value }))}
                      min={3}
                      max={10}
                      step={1}
                      className="w-full"
                    />
                    <p className="text-white/50 text-sm mt-2">
                      Lock account after this many failed attempts
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">IP Rate Limiting</p>
                      <p className="text-white/50 text-sm">Limit requests per IP to prevent abuse</p>
                    </div>
                    <Switch
                      checked={securitySettings.ipRateLimiting}
                      onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, ipRateLimiting: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Require Strong Passwords</p>
                      <p className="text-white/50 text-sm">Minimum 8 chars, uppercase, lowercase, number</p>
                    </div>
                    <Switch
                      checked={securitySettings.requireStrongPassword}
                      onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, requireStrongPassword: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-[#7000FF]/10 border border-[#7000FF]/30">
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-white/50 text-sm">Require 2FA for all users (coming soon)</p>
                    </div>
                    <Switch
                      checked={securitySettings.twoFactorEnabled}
                      onCheckedChange={(checked) => setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: checked }))}
                      disabled
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Info Box */}
            <div className="p-4 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#00F0FF] mt-0.5" />
                <div>
                  <p className="font-medium text-[#00F0FF]">Settings Information</p>
                  <p className="text-white/70 text-sm mt-1">
                    Changes to these settings will take effect immediately after saving. 
                    Some settings may require users to log out and back in to see changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminSettings;
